import jsPDF from 'jspdf';
import { getDisplayName, BATTERY_MODULE_BRUTTO_KWH } from '../config/general-product-config';

const EES_GREEN = [158, 192, 67];
const DARK_TEXT = [50, 55, 60];
const GRAY_TEXT = [120, 120, 120];
const LIGHT_BG = [245, 246, 247];

function addHeader(pdf, title) {
  const pw = pdf.internal.pageSize.getWidth();
  pdf.setFillColor(...EES_GREEN);
  pdf.rect(0, 0, pw, 3, 'F');
  pdf.setFontSize(8);
  pdf.setTextColor(...GRAY_TEXT);
  pdf.text('EES Effiziente Energiespeichersysteme GmbH', pw / 2, 12, { align: 'center' });
  pdf.setFontSize(14);
  pdf.setTextColor(...DARK_TEXT);
  pdf.text(title, pw / 2, 22, { align: 'center' });
  return 30;
}

function addFooter(pdf) {
  const pw = pdf.internal.pageSize.getWidth();
  const ph = pdf.internal.pageSize.getHeight();
  pdf.setFontSize(7);
  pdf.setTextColor(...GRAY_TEXT);
  pdf.text('Unverbindliche Erstempfehlung. Die finale technische Auslegung erfolgt durch EES.', pw / 2, ph - 8, { align: 'center' });
  pdf.text('www.ees-energiespeicher.de | order@ees-energiespeicher.de', pw / 2, ph - 4, { align: 'center' });
}

function checkPage(pdf, y, needed) {
  if (y + needed > pdf.internal.pageSize.getHeight() - 20) {
    pdf.addPage();
    addFooter(pdf);
    return 15;
  }
  return y;
}

function drawKeyValue(pdf, x, y, label, value, w) {
  pdf.setFontSize(7);
  pdf.setTextColor(...GRAY_TEXT);
  pdf.text(label, x, y);
  pdf.setFontSize(9);
  pdf.setTextColor(...DARK_TEXT);
  pdf.text(String(value), x, y + 4.5);
  return w;
}

// ===================== PV KONFIGURATOR PDF =====================
export function exportPvPdf(result) {
  if (!result || !result.solutions?.length) return;
  const pdf = new jsPDF('p', 'mm', 'a4');
  const pw = pdf.internal.pageSize.getWidth();
  const m = 15;
  const cw = pw - m * 2;

  let y = addHeader(pdf, 'PV-Systemempfehlung');

  // Total PV
  pdf.setFontSize(10);
  pdf.setTextColor(...DARK_TEXT);
  pdf.text(`Gesamt-PV-Leistung: ${result.totalPvKw} kWp`, m, y);
  y += 8;

  for (let si = 0; si < result.solutions.length; si++) {
    const sol = result.solutions[si];
    const sys = sol.system;
    const isRec = si === 0;

    y = checkPage(pdf, y, 60);

    // Badge
    pdf.setFillColor(...(isRec ? EES_GREEN : [218, 165, 32]));
    pdf.roundedRect(m, y, isRec ? 48 : 30, 6, 1, 1, 'F');
    pdf.setFontSize(7);
    pdf.setTextColor(255, 255, 255);
    pdf.text(isRec ? 'Empfohlenes System' : 'Alternative', m + 2, y + 4.2);
    y += 10;

    // System name
    pdf.setFontSize(14);
    pdf.setTextColor(...DARK_TEXT);
    pdf.text(sys.displayName || getDisplayName(sys.model), m, y);
    y += 5;

    // Reason
    if (sol.reason) {
      pdf.setFontSize(8);
      pdf.setTextColor(...EES_GREEN);
      pdf.text(sol.reason, m, y);
      y += 6;
    }

    // System specs grid
    y = checkPage(pdf, y, 22);
    pdf.setFillColor(...LIGHT_BG);
    pdf.roundedRect(m, y - 1, cw, 20, 2, 2, 'F');
    const col = cw / 3;
    drawKeyValue(pdf, m + 3, y + 2, 'Empf. PV-Leistung', `${sys.recommendedPvKw} kWp`, col);
    drawKeyValue(pdf, m + 3 + col, y + 2, 'Max. Einspeisung', `${sys.maxFeedInKw} kW`, col);
    drawKeyValue(pdf, m + 3 + col * 2, y + 2, 'Nutzbare Kapazität', `${sys.standardUsableKwh} kWh`, col);
    drawKeyValue(pdf, m + 3, y + 11, 'Max. Entladeleistung', `${sys.maxDischargeKw} kW`, col);
    drawKeyValue(pdf, m + 3 + col, y + 11, 'Netzersatzleistung', `${sys.backupContinuousKva} kVA`, col);
    drawKeyValue(pdf, m + 3 + col * 2, y + 11, 'Netzersatz Peak (30s)', `${sys.backupPeakKva30s} kVA`, col);
    y += 24;

    // Additional battery
    if (sol.additionalBattery) {
      pdf.setFontSize(8);
      pdf.setTextColor(180, 120, 0);
      pdf.text(`+ Empfohlene Zusatzbatterie: ${sol.additionalBattery.modules} x ${BATTERY_MODULE_BRUTTO_KWH} kWh (brutto)`, m, y);
      y += 5;
    }

    // Tracker assignment
    if (sol.assignment?.trackers) {
      y = checkPage(pdf, y, 20);
      pdf.setFontSize(10);
      pdf.setTextColor(...DARK_TEXT);
      pdf.text('Trackerbelegung', m, y);
      y += 5;
      const usedTrackers = sol.assignment.trackers.filter(t => t.assigned?.length > 0);
      for (const t of usedTrackers) {
        y = checkPage(pdf, y, 6);
        pdf.setFontSize(8);
        pdf.setTextColor(...EES_GREEN);
        pdf.text(t.id, m, y);
        pdf.setTextColor(...DARK_TEXT);
        const names = t.assigned.map(s => s.displayName || s.stringName).join(' + ');
        pdf.text(names, m + 12, y);
        pdf.setTextColor(...GRAY_TEXT);
        pdf.text(`${t.totalPowerKw.toFixed(2)} kW | ${t.totalCurrentA.toFixed(1)} A`, pw - m, y, { align: 'right' });
        y += 5;
      }
    }

    // Technical details
    if (sol.assignment?.assigned?.length > 0) {
      y += 2;
      y = checkPage(pdf, y, 15);
      pdf.setFontSize(8);
      pdf.setTextColor(...GRAY_TEXT);
      pdf.text('Technische Details', m, y);
      y += 4;
      const sorted = [...sol.assignment.assigned].sort((a, b) => (a.displayName || '').localeCompare(b.displayName || '', 'de', { numeric: true }));
      for (const s of sorted) {
        y = checkPage(pdf, y, 5);
        pdf.setFontSize(7);
        pdf.setTextColor(...DARK_TEXT);
        pdf.text(`${s.displayName || s.stringName} (${s.moduleCount} Module)`, m, y);
        pdf.setTextColor(...GRAY_TEXT);
        pdf.text(`Voc kalt: ${s.stringVocCold?.toFixed(1)}V | Vmp: ${s.stringVmpEstimate?.toFixed(1)}V | ${s.powerKw?.toFixed(2)} kW`, pw - m, y, { align: 'right' });
        y += 4;
      }
    }

    y += 6;
    pdf.setDrawColor(220, 220, 220);
    pdf.line(m, y, pw - m, y);
    y += 6;
  }

  // Warnings
  if (result.warnings?.length > 0) {
    for (const w of result.warnings) {
      y = checkPage(pdf, y, 8);
      pdf.setFontSize(7);
      pdf.setTextColor(180, 120, 0);
      const lines = pdf.splitTextToSize(w, cw);
      pdf.text(lines, m, y);
      y += lines.length * 3.5 + 2;
    }
  }

  addFooter(pdf);
  pdf.save('EES Konfigurator - PV-Systemempfehlung.pdf');
}

// ===================== ALLGEMEINER KONFIGURATOR PDF =====================
export function exportGeneralPdf(result, inputData) {
  if (!result || result.isProjectPath) return;
  const pdf = new jsPDF('p', 'mm', 'a4');
  const pw = pdf.internal.pageSize.getWidth();
  const m = 15;
  const cw = pw - m * 2;

  let y = addHeader(pdf, 'Ihre Systemempfehlung');

  // Input summary
  pdf.setFontSize(8);
  pdf.setTextColor(...GRAY_TEXT);
  const inputLines = [];
  if (inputData?.customerType) inputLines.push(`Kundentyp: ${inputData.customerType === 'private' ? 'Privat' : 'Gewerbe'}`);
  if (inputData?.annualConsumption) inputLines.push(`Jahresverbrauch: ${Number(inputData.annualConsumption).toLocaleString('de-DE')} kWh`);
  if (inputData?.scenarioType) inputLines.push(`Szenario: ${inputData.scenarioType === 'backup_only' ? 'Notstrom/Backup' : 'Netzparallelbetrieb'}`);
  if (inputLines.length > 0) {
    pdf.text(inputLines.join('  |  '), m, y);
    y += 6;
  }

  // Variant A
  const va = result.variantA;
  pdf.setFillColor(...EES_GREEN);
  pdf.roundedRect(m, y, 48, 6, 1, 1, 'F');
  pdf.setFontSize(7);
  pdf.setTextColor(255, 255, 255);
  pdf.text('Empfohlenes System', m + 2, y + 4.2);
  y += 10;

  pdf.setFontSize(14);
  pdf.setTextColor(...DARK_TEXT);
  pdf.text(va.displayName || getDisplayName(va.model), m, y);
  y += 6;

  pdf.setFontSize(8);
  pdf.setTextColor(...GRAY_TEXT);
  pdf.text(`Systemleistung: ${va.maxDischargeKw} kW | Nutzbare Kapazität: ${va.standardUsableKwh} kWh`, m, y);
  y += 7;

  // Target values
  pdf.setFillColor(...LIGHT_BG);
  pdf.roundedRect(m, y - 1, cw, 12, 2, 2, 'F');
  drawKeyValue(pdf, m + 3, y + 1, 'Empfohlene Leistung', `ca. ${result.targetPowerKw} kW`, cw / 2);
  drawKeyValue(pdf, m + 3 + cw / 2, y + 1, 'Empfohlener Energieinhalt', `ca. ${result.targetEnergyKwh} kWh`, cw / 2);
  y += 16;

  if (va.additionalBattery) {
    pdf.setFontSize(8);
    pdf.setTextColor(180, 120, 0);
    pdf.text(`+ Empfohlene Zusatzbatterie: ${va.additionalBattery.modules} x ${BATTERY_MODULE_BRUTTO_KWH} kWh (brutto)`, m, y);
    y += 6;
  }

  if (result.genLink) {
    pdf.setFontSize(8);
    pdf.setTextColor(...EES_GREEN);
    pdf.text('+ Hans HS GenLink empfohlen', m, y);
    y += 6;
  }

  // Reasoning
  if (result.reasoning) {
    y = checkPage(pdf, y, 15);
    pdf.setFontSize(9);
    pdf.setTextColor(...DARK_TEXT);
    pdf.text('Warum dieses System?', m, y);
    y += 5;
    pdf.setFontSize(8);
    pdf.setTextColor(...GRAY_TEXT);
    const reasonLines = pdf.splitTextToSize(result.reasoning, cw);
    pdf.text(reasonLines, m, y);
    y += reasonLines.length * 3.5 + 4;
  }

  // Variant B
  if (result.variantB) {
    y = checkPage(pdf, y, 25);
    pdf.setDrawColor(220, 220, 220);
    pdf.line(m, y, pw - m, y);
    y += 5;

    pdf.setFillColor(218, 165, 32);
    pdf.roundedRect(m, y, 30, 6, 1, 1, 'F');
    pdf.setFontSize(7);
    pdf.setTextColor(255, 255, 255);
    pdf.text('Alternative', m + 2, y + 4.2);
    y += 10;

    pdf.setFontSize(12);
    pdf.setTextColor(...DARK_TEXT);
    pdf.text(result.variantB.displayName || getDisplayName(result.variantB.model), m, y);
    y += 5;
    pdf.setFontSize(8);
    pdf.setTextColor(...GRAY_TEXT);
    pdf.text(`Systemleistung: ${result.variantB.maxDischargeKw} kW | Nutzbare Kapazität: ${result.variantB.standardUsableKwh} kWh`, m, y);
    y += 5;
    if (result.variantB.additionalBattery) {
      pdf.setTextColor(180, 120, 0);
      pdf.text(`+ ${result.variantB.additionalBattery.modules} x ${BATTERY_MODULE_BRUTTO_KWH} kWh zusätzliche Batterie`, m, y);
      y += 5;
    }
  }

  // Advantages
  if (result.advantages?.length > 0) {
    y += 4;
    y = checkPage(pdf, y, 20);
    pdf.setFontSize(9);
    pdf.setTextColor(...DARK_TEXT);
    pdf.text('Vorteile auf einen Blick', m, y);
    y += 5;
    pdf.setFontSize(7);
    pdf.setTextColor(...GRAY_TEXT);
    for (const a of result.advantages) {
      y = checkPage(pdf, y, 5);
      pdf.text(`\u2713  ${a}`, m + 2, y);
      y += 4;
    }
  }

  // Warnings
  if (result.warnings?.length > 0) {
    y += 4;
    for (const w of result.warnings) {
      y = checkPage(pdf, y, 8);
      pdf.setFontSize(7);
      pdf.setTextColor(180, 120, 0);
      const lines = pdf.splitTextToSize(w, cw);
      pdf.text(lines, m, y);
      y += lines.length * 3.5 + 2;
    }
  }

  addFooter(pdf);
  pdf.save('EES Konfigurator - Systemempfehlung.pdf');
}
